import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, Globe } from 'lucide-react';
import { getCart } from '../mock';
import { useTranslation } from '../LanguageContext';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, setLang, currency, setCurrency } = useTranslation();

  useEffect(() => {
    const update = () => setCartCount(getCart().length);
    update();
    window.addEventListener('cart-updated', update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('cart-updated', update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const link = (to, label) => {
    const active = pathname === to;
    return (
      <Link
        to={to}
        onClick={() => setMobileOpen(false)}
        className={`transition ${active ? 'text-[color:var(--burgundy)]' : 'text-[color:var(--ink-soft)] hover:text-[color:var(--burgundy)]'}`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="w-full sticky top-0 z-40 backdrop-blur-md bg-[rgba(243,237,231,0.85)] border-b border-transparent">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="leading-tight">
          <div className="font-serif text-[color:var(--burgundy)] text-lg md:text-xl tracking-wide">Italian Digital Store</div>
          <div className="font-serif italic text-[color:var(--ink-soft)] text-xs md:text-sm">Inviti Digitali Unici</div>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {link('/', t('nav.home'))}
          {link('/template', t('nav.templates'))}
          {link('/prezzi', t('nav.pricing'))}
          {link('/crea', t('nav.builder'))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language / Currency toggle */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[color:var(--border)] text-xs text-[color:var(--ink-soft)] hover:border-[color:var(--burgundy)] transition"
            >
              <Globe size={13} />
              <span className="font-medium">{lang.toUpperCase()}</span>
              <span className="opacity-60">•</span>
              <span>{currency}</span>
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-[color:var(--border)] rounded-xl shadow-xl z-20 overflow-hidden">
                  <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)] border-b border-[color:var(--border)]">{t('common.language')}</div>
                  <button onClick={() => { setLang('it'); setMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--cream-2)] flex items-center justify-between ${lang === 'it' ? 'text-[color:var(--burgundy)] font-medium' : ''}`}>🇮🇹 Italiano {lang === 'it' && <span>✓</span>}</button>
                  <button onClick={() => { setLang('en'); setMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--cream-2)] flex items-center justify-between ${lang === 'en' ? 'text-[color:var(--burgundy)] font-medium' : ''}`}>🇬🇧 English {lang === 'en' && <span>✓</span>}</button>
                  <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)] border-t border-b border-[color:var(--border)]">{t('common.currency')}</div>
                  <button onClick={() => { setCurrency('EUR'); setMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--cream-2)] flex items-center justify-between ${currency === 'EUR' ? 'text-[color:var(--burgundy)] font-medium' : ''}`}>€ Euro (EUR) {currency === 'EUR' && <span>✓</span>}</button>
                  <button onClick={() => { setCurrency('USD'); setMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--cream-2)] flex items-center justify-between ${currency === 'USD' ? 'text-[color:var(--burgundy)] font-medium' : ''}`}>$ US Dollar (USD) {currency === 'USD' && <span>✓</span>}</button>
                </div>
              </>
            )}
          </div>

          <Link to="/carrello" className="relative p-2 text-[color:var(--ink-soft)] hover:text-[color:var(--burgundy)] transition">
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[color:var(--burgundy)] text-[#f8ede0] text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {cartCount}
              </span>
            )}
          </Link>
          <Link to="/crea" className="btn-primary hidden sm:inline-flex">{t('nav.create')}</Link>
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[color:var(--border)] px-6 py-4 flex flex-col gap-4 bg-[color:var(--cream)]">
          {link('/', t('nav.home'))}
          {link('/template', t('nav.templates'))}
          {link('/prezzi', t('nav.pricing'))}
          {link('/crea', t('nav.builder'))}
          {link('/carrello', t('nav.cart'))}
        </div>
      )}
    </header>
  );
}
